import moment from 'moment';
import { forwardRef, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationIcon, ProfitIcon, LossIcon } from '../../../assets/icons';
import { AppContext } from '../../../context/ContextProvider';
import { getImageUrl, getRegionColors } from '../../../helpers';
import { Product } from '../../../types/productType';
import { capitalizeFirstLetter, classNames, roundNumber } from '../../../utils';
import { MaturityStatus } from '../constants/maturityWindows';
import { PortalHolding } from '../hooks/usePortalHoldings';
import QualityRadarChart from './QualityRadarChart';

/** Get score color based on value */
const getScoreStyle = (score: number): { bg: string; color: string } => {
  if (score >= 96) return { bg: '#16a34a', color: '#ffffff' };
  if (score >= 90) return { bg: '#22c55e', color: '#ffffff' };
  if (score >= 80) return { bg: '#eab308', color: '#ffffff' };
  return { bg: '#9ca3af', color: '#ffffff' };
};

/** Get score label based on Wine Advocate rating system */
const getScoreLabel = (score: number): string => {
  if (score >= 96) return 'Extraordinary';
  if (score >= 90) return 'Outstanding';
  if (score >= 80) return 'Very Good';
  return 'Below Average';
};

/** Get score description for tooltip based on Wine Advocate rating system */
const getScoreDescription = (score: number): { range: string; description: string } => {
  if (score >= 96) {
    return {
      range: '96-100',
      description:
        'An extraordinary wine of profound and complex character displaying all the attributes expected of a classic wine of its variety. Worth a special effort to find.',
    };
  }
  if (score >= 90) {
    return {
      range: '90-95',
      description: 'An outstanding wine of exceptional complexity and character. In short, these are terrific wines.',
    };
  }
  if (score >= 80) {
    return {
      range: '80-89',
      description:
        'A very good wine displaying various degrees of finesse and flavor as well as character with no noticeable flaws.',
    };
  }
  return {
    range: 'Below 80',
    description: 'A wine with noticeable flaws or lacking in character.',
  };
};

interface WineCardInsights {
  maturity?: MaturityStatus;
  demandSignal?: string;
  performanceNote?: string;
}

interface CellarWineCardProps {
  item: Product;
  onClick?: () => void;
  className?: string;
  insights?: WineCardInsights;
  onDismissInsight?: (type: 'maturity' | 'demand' | 'performance') => void;
  marketData?: PortalHolding;
}

// Review type for carousel
interface Review {
  source: string;
  sourceShort: string;
  sourceColor: string;
  reviewer: string | null;
  publishedDate: string | null;
  score: number | null;
  drinkFrom: number | null;
  drinkTo: number | null;
  tastingNote: string | null;
  externalLink: string | null;
}

const CellarWineCard = forwardRef<HTMLDivElement, CellarWineCardProps>(
  ({ item, onClick, className, marketData }, ref) => {
    const { t } = useTranslation();
    const { gbpToTargetCurrency } = useContext(AppContext);
    const [showReviews, setShowReviews] = useState(false);
    const [showMarketMetrics, setShowMarketMetrics] = useState(false);
    const [showScoreTooltip, setShowScoreTooltip] = useState(false);
    const [currentReviewIndex, setCurrentReviewIndex] = useState(0);

    // Build reviews array for carousel
    const reviews = useMemo(() => {
      const reviewList: Review[] = [];

      // Wine Advocate review
      if (marketData?.waReviewer || marketData?.waScore) {
        reviewList.push({
          source: 'The Wine Advocate',
          sourceShort: 'RP',
          sourceColor: '#FCD34D', // amber/yellow
          reviewer: marketData.waReviewer,
          publishedDate: marketData.waScoreDate,
          score: marketData.waScore,
          drinkFrom: marketData.waDrinkYearMin,
          drinkTo: marketData.waDrinkYearMax,
          tastingNote: marketData.waTastingNote,
          externalLink: marketData.waArticleUrl,
        });
      }

      // Vinous review
      if (marketData?.vReviewer || marketData?.vScoreMedian) {
        reviewList.push({
          source: 'Vinous',
          sourceShort: 'V',
          sourceColor: '#722F37', // wine red
          reviewer: marketData.vReviewer,
          publishedDate: marketData.vReviewDate,
          score: marketData.vScoreMedian,
          drinkFrom: marketData.vDrinkFrom,
          drinkTo: marketData.vDrinkTo,
          tastingNote: marketData.vTastingNote,
          externalLink: marketData.vExternalLink,
        });
      }

      return reviewList;
    }, [marketData]);

    const hasReviews = reviews.length > 0;

    const { color: regionColor, textColor } = useMemo(
      () => getRegionColors(item.cultWinesAllocationRegion),
      [item.cultWinesAllocationRegion],
    );

    const formattedValues = useMemo(() => {
      const pnlPercentage = item.totalCost > 0 ? ((item.profitAndLoss || 0) / item.totalCost) * 100 : 0;
      return {
        totalValue: gbpToTargetCurrency.convertAndFormat(item.valuePerUnit || 0),
        profitAndLoss: gbpToTargetCurrency.convertAndFormat(Math.abs(item.profitAndLoss || 0)),
        pnlPercentage: `${pnlPercentage >= 0 ? '+' : ''}${roundNumber(pnlPercentage, 1)}%`,
        trend: (item.profitAndLoss || 0) >= 0 ? 'up' : 'down',
      };
    }, [item, gbpToTargetCurrency]);

    const scoreInfo = useMemo(() => {
      if (!item.combinedScore) return null;
      const style = getScoreStyle(item.combinedScore);
      return {
        score: item.combinedScore,
        bgColor: style.bg,
        textColor: style.color,
        label: getScoreLabel(item.combinedScore),
      };
    }, [item.combinedScore]);

    const name = `${item.vintage ?? ''} ${item.wineName}`.trim();
    const qtyString = `${item.qty}x (${item.unit})`;
    const itemImage = getImageUrl(item.imageFileName || '', { height: 200 });

    // Show metrics button if we have any market data at all
    const hasMarketData = !!marketData;

    // Close button component for overlays
    const CloseButton = ({ onClose }: { onClose: () => void }) => (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-900 dark:bg-slate-600 hover:bg-gray-800 dark:hover:bg-slate-500 transition-colors shadow-lg"
      >
        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    );

    return (
      <div
        ref={ref}
        onClick={onClick}
        className={classNames(
          'relative flex cursor-pointer bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl overflow-hidden transition-all duration-200 ease-out shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-gray-200 dark:hover:border-slate-600 min-w-[320px]',
          className || 'w-full',
        )}
      >
        {/* Reviews Overlay with Carousel */}
        {hasReviews && (
          <div
            className={classNames(
              'absolute inset-0 z-20 flex flex-col bg-white dark:bg-slate-800 rounded-2xl transition-all duration-300 ease-out overflow-hidden',
              showReviews ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none',
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with navigation */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-700">
              <div className="flex items-center gap-2">
                {reviews.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setCurrentReviewIndex((prev) => (prev === 0 ? reviews.length - 1 : prev - 1))}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                  >
                    <svg
                      className="w-4 h-4 text-gray-600 dark:text-gray-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="text-center">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                    Critic Reviews
                    {reviews[currentReviewIndex]?.source === 'The Wine Advocate' && marketData?.wa != null && (
                      <span className="ml-1 text-gray-500 dark:text-gray-400 font-normal">({marketData.wa})</span>
                    )}
                    {reviews[currentReviewIndex]?.source === 'Vinous' && marketData?.vinous != null && (
                      <span className="ml-1 text-gray-500 dark:text-gray-400 font-normal">({marketData.vinous}v)</span>
                    )}
                  </h3>
                  {reviews.length > 1 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {currentReviewIndex + 1} of {reviews.length}
                    </p>
                  )}
                </div>
                {reviews.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setCurrentReviewIndex((prev) => (prev === reviews.length - 1 ? 0 : prev + 1))}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                  >
                    <svg
                      className="w-4 h-4 text-gray-600 dark:text-gray-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>
              <CloseButton onClose={() => setShowReviews(false)} />
            </div>

            {/* Current Review */}
            <div className="flex-1 overflow-y-auto">
              {reviews[currentReviewIndex] && (
                <ReviewCard review={reviews[currentReviewIndex]} wa={marketData?.wa} vinous={marketData?.vinous} />
              )}
            </div>

            {/* Carousel dots */}
            {reviews.length > 1 && (
              <div className="flex justify-center gap-1.5 py-2 border-t border-gray-100 dark:border-slate-700">
                {reviews.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setCurrentReviewIndex(index)}
                    className={classNames(
                      'w-2 h-2 rounded-full transition-all',
                      index === currentReviewIndex
                        ? 'bg-orange dark:bg-teal-400 w-4'
                        : 'bg-gray-300 dark:bg-slate-600 hover:bg-gray-400 dark:hover:bg-slate-500',
                    )}
                  />
                ))}
              </div>
            )}

            {/* Footer */}
            <div className="px-4 py-2 bg-gray-50 dark:bg-slate-700 border-t border-gray-100 dark:border-slate-600">
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium truncate">{name}</p>
            </div>
          </div>
        )}

        {/* Market Metrics Overlay */}
        {hasMarketData && (
          <div
            className={classNames(
              'absolute inset-0 z-20 flex flex-col bg-white dark:bg-slate-800 rounded-2xl transition-all duration-300 ease-out overflow-hidden',
              showMarketMetrics ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none',
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-vine/10 to-teal-50 dark:from-vine/20 dark:to-teal-900/30 border-b border-vine/20 dark:border-vine/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-vine flex items-center justify-center shadow-md">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">Market Metrics</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Quality & Performance</p>
                </div>
              </div>
              <CloseButton onClose={() => setShowMarketMetrics(false)} />
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Radar Chart */}
              <div className="flex justify-center mb-4">
                <QualityRadarChart
                  vintage={marketData.vintageScore ?? 0}
                  critic={marketData.normalizedCriticScore ?? 0}
                  fairValue={marketData.fairValueScore ?? 0}
                  liquidity={marketData.liquidityScore ?? 0}
                  performance={marketData.performanceScore ?? 0}
                  size={140}
                />
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 gap-2">
                <MetricCard
                  label="Vintage Score"
                  value={marketData.vintageScore ? `${Number(marketData.vintageScore).toFixed(0)}` : '-'}
                  suffix="/100"
                />
                <MetricCard
                  label="Normalised Critic Score"
                  value={
                    marketData.normalizedCriticScore ? `${Number(marketData.normalizedCriticScore).toFixed(0)}` : '-'
                  }
                  suffix="/100"
                />
                <MetricCard
                  label="Fair Value Score"
                  value={marketData.fairValueScore ? `${Number(marketData.fairValueScore).toFixed(0)}` : '-'}
                  suffix="/100"
                />
                <MetricCard
                  label="Liquidity Score"
                  value={marketData.liquidityScore ? `${Number(marketData.liquidityScore).toFixed(0)}` : '-'}
                  suffix="/100"
                />
                <MetricCard
                  label="Performance Score"
                  value={marketData.performanceScore ? `${marketData.performanceScore}` : '-'}
                  suffix="/100"
                />
                <MetricCard
                  label="Overall Grade"
                  value={marketData.grade ? `${roundNumber(marketData.grade, 1)}` : '-'}
                  suffix="/10"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-3 bg-gray-50 dark:bg-slate-700 border-t border-gray-100 dark:border-slate-600">
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium truncate">{name}</p>
            </div>
          </div>
        )}

        {/* Region accent bar */}
        <div className="w-1.5 flex-shrink-0" style={{ backgroundColor: regionColor || '#757587' }} />

        {/* Main Content */}
        <div className="flex flex-col flex-1 p-4">
          {/* Wine Image */}
          <div className="bg-gradient-to-b from-gray-50 to-gray-100 dark:from-slate-700 dark:to-slate-600 rounded-xl flex items-center justify-center mb-3 py-3">
            <img className="h-[150px] object-contain drop-shadow-md" alt={name} src={itemImage} />
          </div>

          {/* Wine Name */}
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white leading-snug mb-2">{name}</h3>

          {/* Region & Quantity */}
          <div className="flex items-center gap-2 mb-3">
            <span
              style={{ backgroundColor: regionColor || '#757587' }}
              className={classNames('px-2.5 py-1 rounded-md text-xs font-semibold', textColor)}
            >
              {capitalizeFirstLetter(item.cultWinesAllocationRegion || 'Other')}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{qtyString}</span>
          </div>

          {/* Value Card */}
          <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3 mb-3">
            <div className="flex justify-between items-center mb-2">
              <span className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {t('common:totalValue')}
              </span>
              <span className="text-base font-bold text-gray-900 dark:text-white">{formattedValues.totalValue}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {t('common:profitLoss')}
              </span>
              <div className="flex items-center gap-1">
                {formattedValues.trend === 'up' ? (
                  <ProfitIcon className="w-3.5 h-3.5" />
                ) : (
                  <LossIcon className="w-3.5 h-3.5" />
                )}
                <span
                  className={classNames(
                    'text-sm font-semibold',
                    formattedValues.trend === 'up' ? 'text-green-600' : 'text-red-500',
                  )}
                >
                  {formattedValues.trend === 'up' ? '+' : '-'}
                  {formattedValues.profitAndLoss}
                </span>
                <span
                  className={classNames('text-xs', formattedValues.trend === 'up' ? 'text-green-500' : 'text-red-400')}
                >
                  ({formattedValues.pnlPercentage})
                </span>
              </div>
            </div>
          </div>

          {/* Location */}
          {item.location && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-1">
              <LocationIcon className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{item.location}</span>
            </div>
          )}

          {/* Deal Info */}
          <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 text-[10px] text-gray-400 dark:text-gray-500 mb-3">
            {item.dealDate && <span>{moment(item.dealDate).format('DD MMM YYYY').toUpperCase()}</span>}
            {item.dealDate && (item.dealRef || item.rotationNumber) && <span>•</span>}
            {item.dealRef && <span>{item.dealRef}</span>}
            {item.rotationNumber && (
              <>
                {item.dealRef && <span>•</span>}
                <span>{item.rotationNumber}</span>
              </>
            )}
          </div>

          {/* Action Bar */}
          <div className="mt-auto pt-3 border-t border-gray-100 dark:border-slate-700">
            {/* Score and Quick Actions Row */}
            <div className="flex items-center justify-between gap-2 mb-3">
              {/* Score Badge with Tooltip */}
              {scoreInfo ? (
                <div
                  className="relative flex items-center gap-2 cursor-help"
                  onMouseEnter={() => setShowScoreTooltip(true)}
                  onMouseLeave={() => setShowScoreTooltip(false)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="w-9 h-9 rounded-lg font-bold text-sm flex items-center justify-center shadow-sm"
                    style={{ backgroundColor: scoreInfo.bgColor, color: scoreInfo.textColor }}
                  >
                    {scoreInfo.score}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">{scoreInfo.label}</p>
                    <p className="text-[10px] text-gray-400 dark:text-gray-500">Critic Score</p>
                  </div>

                  {/* Score Tooltip */}
                  {showScoreTooltip && (
                    <div className="absolute z-30 bottom-full left-0 mb-2 w-64 p-3 bg-gray-900 dark:bg-slate-700 text-white rounded-xl shadow-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="w-8 h-8 rounded-lg font-bold text-sm flex items-center justify-center"
                          style={{ backgroundColor: scoreInfo.bgColor, color: scoreInfo.textColor }}
                        >
                          {scoreInfo.score}
                        </div>
                        <div>
                          <p className="text-sm font-bold">{scoreInfo.label}</p>
                          <p className="text-[10px] text-gray-400">{getScoreDescription(scoreInfo.score).range}</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-300 leading-relaxed">
                        {getScoreDescription(scoreInfo.score).description}
                      </p>
                      <div className="absolute top-full left-4 border-8 border-transparent border-t-gray-900 dark:border-t-slate-700" />
                    </div>
                  )}
                </div>
              ) : (
                <div />
              )}

              {/* Quick Action Buttons */}
              <div className="flex items-center gap-1.5">
                {hasMarketData && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMarketMetrics(true);
                    }}
                    className="px-3 py-1.5 text-[11px] font-semibold text-white bg-vine hover:bg-vine/90 rounded-md shadow-sm transition-all active:scale-95"
                  >
                    Metrics
                  </button>
                )}

                {hasReviews && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowReviews(true);
                    }}
                    className="px-3 py-1.5 text-[11px] font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-md transition-all active:scale-95"
                  >
                    Reviews
                  </button>
                )}
              </div>
            </div>

            {/* View Details - clickable area indicator */}
            <div className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-vine transition-colors group cursor-pointer">
              <span>View Details</span>
              <svg
                className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

// Review Card Component for the overlay (carousel item)
const ReviewCard = ({ review, wa, vinous }: { review: Review; wa?: number | null; vinous?: string | null }) => {
  const formattedDate = review.publishedDate ? moment(review.publishedDate).format('MMM D, YYYY') : null;
  const drinkWindow =
    review.drinkFrom && review.drinkTo
      ? `${review.drinkFrom} - ${review.drinkTo}`
      : review.drinkFrom
      ? `From ${review.drinkFrom}`
      : null;

  const isWineAdvocate = review.source === 'The Wine Advocate';

  return (
    <div className="flex flex-col h-full">
      {/* Source Header with Score */}
      <div className="flex items-center justify-between px-4 py-2" style={{ backgroundColor: review.sourceColor }}>
        <span className={classNames('text-sm font-semibold', isWineAdvocate ? 'text-gray-900' : 'text-white')}>
          {review.source}
        </span>
        {isWineAdvocate && wa != null && (
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/20 shadow-md">
            <span
              className={classNames('text-sm font-bold leading-none', isWineAdvocate ? 'text-gray-900' : 'text-white')}
            >
              {wa}
            </span>
          </div>
        )}
        {!isWineAdvocate && vinous != null && (
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/20 shadow-md">
            <span className="text-sm font-bold text-white leading-none">{vinous}</span>
          </div>
        )}
      </div>

      {/* Review Details */}
      <div className="px-4 py-3 bg-amber-50/50 dark:bg-slate-700/30 border-b border-amber-200/50 dark:border-slate-600">
        <div className="grid grid-cols-3 gap-4 text-sm">
          {/* Reviewed by */}
          <div>
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-0.5">Reviewed by:</p>
            <p className="text-sm text-gray-900 dark:text-white font-medium">{review.reviewer || 'N/A'}</p>
          </div>
          {/* Release Price */}
          <div>
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-0.5">Release Price:</p>
            <p className="text-sm text-gray-900 dark:text-white font-medium">N/A</p>
          </div>
          {/* Drink Date */}
          <div>
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-0.5">Drink Date:</p>
            <p className="text-sm text-gray-900 dark:text-white font-medium">{drinkWindow || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Tasting Note */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {review.tastingNote ? (
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{review.tastingNote}</p>
        ) : (
          <p className="text-sm text-gray-400 dark:text-gray-500 italic">No tasting notes available.</p>
        )}
      </div>

      {/* Footer with Published Date */}
      {formattedDate && (
        <div className="px-4 py-2 border-t border-gray-100 dark:border-slate-700">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            <span className="font-semibold">Published:</span> {formattedDate}
          </p>
        </div>
      )}
    </div>
  );
};

// Metric Card Component for the overlay
const MetricCard = ({
  label,
  value,
  suffix,
  highlight,
}: {
  label: string;
  value: string;
  suffix?: string;
  highlight?: boolean;
}) => (
  <div
    className={classNames(
      'rounded-lg p-3',
      highlight ? 'bg-vine/10 dark:bg-teal-500/20' : 'bg-gray-50 dark:bg-slate-700',
    )}
  >
    <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">{label}</p>
    <div className="flex items-baseline gap-1">
      <span
        className={classNames(
          'text-sm font-bold',
          highlight ? 'text-vine dark:text-teal-400' : 'text-gray-900 dark:text-white',
        )}
      >
        {value}
      </span>
      {suffix && value !== '-' && <span className="text-[10px] text-gray-400 dark:text-gray-500">{suffix}</span>}
    </div>
  </div>
);

CellarWineCard.displayName = 'CellarWineCard';

export default CellarWineCard;
